import { Breadcrumb } from '@components/breadcrumb';
import { useCallback, useEffect, useRef } from 'react';
import { useFragment } from 'react-relay';
import { TypeMap, fundsDetailPageGFragment } from './lib';
import type { FundsDetailsPageProps } from './types';

export const FundsDetailsPage: React.FC<FundsDetailsPageProps> = ({ page: pageKey, path, location, currentLanguage }) => {
  const page = useFragment(fundsDetailPageGFragment, pageKey);

  const recalcCountsRef = useRef(10);
  const recalcResettableRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const recalcTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const elementRefsRef = useRef<{
    base: Element | null;
    title: Element | null;
    selector: HTMLElement | null;
  }>({
    base: null,
    title: null,
    selector: null,
  });
  const defaultMarginTop = '3.5em';

  // Check if page data is valid
  const hasValidPage = page?.components?.edges;

  const getRefs = useCallback(() => {
    elementRefsRef.current.base = containerRef.current;
    if (!elementRefsRef.current.base) {
      return;
    }

    const title = elementRefsRef.current.base.getElementsByClassName('Base-title');
    const selector = elementRefsRef.current.base.getElementsByClassName('tranche-selector-wrapper');
    elementRefsRef.current.title = title?.length ? title[0] : null;
    elementRefsRef.current.selector = selector?.length ? (selector[0] as HTMLElement) : null;
  }, []);

  const recalculateSelector = useCallback(() => {
    if (recalcTimeoutRef.current) {
      clearTimeout(recalcTimeoutRef.current);
      recalcTimeoutRef.current = null;
    }
    if (!document.body || !document.body.offsetWidth || document.body.offsetWidth > 1100) {
      if (recalcResettableRef.current && elementRefsRef.current.selector) {
        elementRefsRef.current.selector.style.marginTop = '';
        recalcResettableRef.current = false;
      }
      return;
    }

    if ((!elementRefsRef.current.base || !elementRefsRef.current.title || !elementRefsRef.current.selector) && !recalcTimeoutRef.current) {
      if (recalcCountsRef.current <= 0) {
        return;
      }
      recalcCountsRef.current -= 1;
      getRefs();
      recalcTimeoutRef.current = setTimeout(recalculateSelector, 100);
      return;
    }

    const titleElement = elementRefsRef.current.title as HTMLElement;
    if (!titleElement) {
      return;
    }

    const titleHeight = titleElement.offsetHeight;
    let diff = titleHeight - 36;
    let newMarginTop = defaultMarginTop;
    if (diff < 0) {
      diff = 0;
    }

    if (diff) {
      newMarginTop = `calc( ${defaultMarginTop} + ${diff}px )`;
    }
    recalcResettableRef.current = true;
    if (elementRefsRef.current.selector) {
      elementRefsRef.current.selector.style.marginTop = newMarginTop;
    }
  }, [getRefs]);

  const onResize = useCallback(() => {
    if (recalcTimeoutRef.current) {
      clearTimeout(recalcTimeoutRef.current);
      recalcTimeoutRef.current = null;
    }

    recalcCountsRef.current = 10;
    recalculateSelector();
  }, [recalculateSelector]);

  // Replace componentWillMount - scroll to top on mount
  useEffect(() => {
    document.getElementsByTagName('html')[0].scrollTop = 0;
    document.getElementsByTagName('body')[0].scrollTop = 0;
  }, []);

  // Replace componentDidMount - setup refs, resize listener, and title width
  useEffect(() => {
    getRefs();
    setTimeout(recalculateSelector, 1);
    window.addEventListener('resize', onResize);

    // Not a nice solution, but a solution to bring title to full width
    // when no tranche selector is present.
    const trancheSelector = document.querySelector('.tranche-selector-wrapper .LabeledSelectBox');
    const title = document.querySelector('.Introduction .Base-title');
    if (!trancheSelector && title) {
      title.classList.add('-full-width');
    }

    // Cleanup: replace componentWillUnmount
    return () => {
      window.removeEventListener('resize', onResize);
      if (recalcTimeoutRef.current) {
        clearTimeout(recalcTimeoutRef.current);
      }
    };
  }, [getRefs, recalculateSelector, onResize]);

  // Early return if page data is invalid (after all hooks)
  if (!hasValidPage) {
    return null;
  }

  return (
    <div ref={containerRef}>
      <Breadcrumb data={page.breadcrumbs} />
      <div className="relative mx-auto flex w-full max-w-[820px] flex-wrap items-start justify-between pt-20 max-[1100px]:w-[calc(100vw-80px)] max-[1100px]:max-w-[565px] max-[600px]:w-[calc(100vw-40px)] max-[600px]:pt-10">
        {page.components.edges?.map((edge: any) => {
          if (!edge?.node) {
            return null;
          }
          const Type = TypeMap[edge.node.__typename];
          if (!Type) {
            // Skip unknown component types
            return null;
          }
          return (
            <Type
              key={edge.node.id}
              path={location.pathname}
              location={location}
              component={edge.node}
              largeinvestmentfonds={page.largeinvestmentfonds}
              currentLanguage={currentLanguage}
              reportCurrency={edge.node.__typename === 'PerformanceOverview' ? page.reportcurrency : null}
              reportcurrency={edge.node.__typename === 'PerformanceOverview' ? page.reportcurrency : null}
              margins={{ top: 0, right: 5, bottom: 0, left: 5 }}
            />
          );
        })}
      </div>
    </div>
  );
};
